import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import 'iconify-icon';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';
const authHeaders = () => {
  const t = localStorage.getItem('access_token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

// Collections we expect to have content after a successful prod sync.
const CRITICAL_COLLECTIONS = [
  'franchises', 'team_members', 'eneaflix', 'events',
  'blog_posts', 'testimonials', 'faqs',
];

const SystemDiagnostics = () => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [lastSyncResult, setLastSyncResult] = useState(null);
  // Global site settings (WhatsApp number, GTM/GA IDs, etc.)
  const [settings, setSettings] = useState({
    whatsapp_number: '',
    gtm_id: '',
    ga_id: '',
  });
  const [savingSettings, setSavingSettings] = useState(false);

  const loadSettings = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/admin/settings`, {
        withCredentials: true,
        headers: authHeaders(),
      });
      // Merge over the public defaults so the input isn't empty on
      // first render before any admin ever saved a value.
      const pub = await axios.get(`${API_URL}/public/settings`);
      setSettings({ ...pub.data, ...(res.data || {}) });
    } catch (err) {
      console.error('settings load failed:', err);
    }
  }, []);

  const saveWhatsappNumber = async () => {
    const raw = (settings.whatsapp_number || '').trim();
    // Basic sanity: at least 10 digits (DDD + 8/9 digits).
    const digits = raw.replace(/\D/g, '');
    if (digits.length < 10) {
      toast.error('Número inválido — inclua DDD + número (ex: +5547999201212)');
      return;
    }
    setSavingSettings(true);
    try {
      await axios.put(
        `${API_URL}/admin/settings`,
        { whatsapp_number: raw },
        { withCredentials: true, headers: authHeaders() }
      );
      toast.success('Número atualizado! Recarregue a página pública para ver o novo link.');
    } catch (err) {
      console.error(err);
      toast.error('Falha ao salvar configuração');
    } finally {
      setSavingSettings(false);
    }
  };

  const saveAnalytics = async () => {
    const gtm = (settings.gtm_id || '').trim();
    const ga = (settings.ga_id || '').trim();
    if (gtm && !/^GTM-[A-Z0-9]+$/.test(gtm)) {
      toast.error('GTM ID inválido — formato esperado: GTM-XXXXXXX');
      return;
    }
    if (ga && !/^G-[A-Z0-9]+$/.test(ga)) {
      toast.error('GA4 ID inválido — formato esperado: G-XXXXXXXXXX');
      return;
    }
    setSavingSettings(true);
    try {
      await axios.put(
        `${API_URL}/admin/settings`,
        { gtm_id: gtm, ga_id: ga },
        { withCredentials: true, headers: authHeaders() }
      );
      toast.success('IDs atualizados. Recarregue o site público para o novo GTM começar a rodar.');
    } catch (err) {
      console.error(err);
      toast.error('Falha ao salvar configuração');
    } finally {
      setSavingSettings(false);
    }
  };

  const loadStatus = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_URL}/admin/system/db-status`, {
        withCredentials: true,
        headers: authHeaders(),
      });
      setStatus(res.data);
    } catch (err) {
      console.error('db-status failed:', err);
      toast.error('Falha ao carregar diagnóstico');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadStatus();
    loadSettings();
  }, [loadStatus, loadSettings]);

  const runSync = async ({ reset }) => {
    const label = reset ? 'Re-sincronização completa' : 'Sincronização incremental';
    if (reset && !window.confirm(
      `${label}: isto limpa o registro de migrations aplicadas e reexecuta TODAS ` +
      '(idempotente — não duplica dados). Continuar?'
    )) {
      return;
    }
    setSyncing(true);
    try {
      const params = reset
        ? '?reset=true'
        : '?only=0010_full_preview_sync';
      const res = await axios.post(
        `${API_URL}/admin/system/run-migrations${params}`,
        {},
        { withCredentials: true, headers: authHeaders() }
      );
      setLastSyncResult(res.data);
      const deltaCount = Object.keys(res.data.deltas || {}).length;
      toast.success(`${label} concluída — ${deltaCount} coleção(ões) alterada(s)`);
      await loadStatus();
    } catch (err) {
      console.error('run-migrations failed:', err);
      toast.error('Falha ao executar sincronização: ' + (err.response?.data?.detail || err.message));
    } finally {
      setSyncing(false);
    }
  };

  if (loading && !status) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="system-loading">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-black"></div>
      </div>
    );
  }

  const counts = status?.counts || {};
  const syncFilesCount = (status?.sync_data_files || []).length;
  const syncDirOk = status?.sync_data_dir_exists;
  const emptyCritical = CRITICAL_COLLECTIONS.filter(
    (c) => (typeof counts[c] === 'number' ? counts[c] : 0) === 0
  );
  const totalApplied = (status?.applied_migrations || []).length;
  const m0010Applied = (status?.applied_migrations || []).some(
    (m) => m._id === '0010_full_preview_sync'
  );

  const healthOk = syncDirOk && emptyCritical.length === 0 && m0010Applied;

  return (
    <div data-testid="system-diagnostics-page">
      <div className="mb-8">
        <h2 className="text-3xl font-bold mb-2">Diagnóstico do Sistema</h2>
        <p className="text-neutral-600">
          Verifique o estado do banco de dados de produção e force a
          sincronização de dados a partir dos snapshots commitados no repositório.
        </p>
      </div>

      {/* Global settings — editable WhatsApp number */}
      <div className="bg-white rounded-xl border border-neutral-200 p-6 mb-8" data-testid="global-settings-card">
        <div className="flex items-start gap-3 mb-4">
          <iconify-icon icon="mdi:whatsapp" className="text-3xl text-[#25D366] mt-0.5"></iconify-icon>
          <div className="flex-1">
            <h3 className="text-lg font-bold">Configurações Globais</h3>
            <p className="text-sm text-neutral-500">
              Este número é usado no <strong>botão flutuante</strong>, no header ("FALE CONOSCO"),
              no rodapé e em todos os CTAs globais do site. Franquias com WhatsApp próprio cadastrado
              não são afetadas — usam o número da própria unidade.
            </p>
          </div>
        </div>
        <div className="flex flex-col md:flex-row gap-3">
          <div className="flex-1">
            <label htmlFor="whatsapp_number" className="block text-xs font-mono uppercase tracking-widest text-neutral-500 mb-1">
              Número de WhatsApp global
            </label>
            <input
              id="whatsapp_number"
              type="tel"
              data-testid="settings-whatsapp-input"
              value={settings.whatsapp_number || ''}
              onChange={(e) => setSettings((s) => ({ ...s, whatsapp_number: e.target.value }))}
              placeholder="+5547999201212"
              className="w-full px-4 py-2.5 border border-neutral-300 rounded-lg font-mono text-sm focus:border-accent-orange focus:ring-1 focus:ring-accent-orange outline-none"
            />
            <p className="text-xs text-zinc-700 mt-1">
              Formato: <code>+55DDNNNNNNNNN</code> (código do país + DDD + número). Máscaras
              como parênteses e hífens são aceitas.
            </p>
          </div>
          <button
            type="button"
            onClick={saveWhatsappNumber}
            disabled={savingSettings}
            data-testid="settings-whatsapp-save-btn"
            className="self-end px-6 py-2.5 bg-accent-orange text-white rounded-lg text-sm font-bold hover:bg-[#E55D00] transition-colors disabled:opacity-50"
          >
            {savingSettings ? 'Salvando…' : 'Salvar'}
          </button>
        </div>
      </div>

      {/* Analytics — GTM + GA4 IDs (editable, no redeploy needed) */}
      <div className="bg-white rounded-xl border border-neutral-200 p-6 mb-8" data-testid="analytics-settings-card">
        <div className="flex items-start gap-3 mb-4">
          <iconify-icon icon="mdi:google-analytics" className="text-3xl text-[#F9AB00] mt-0.5"></iconify-icon>
          <div className="flex-1">
            <h3 className="text-lg font-bold">Analytics (GTM + GA4)</h3>
            <p className="text-sm text-neutral-500">
              O <strong>Google Tag Manager</strong> gerencia todos os scripts de rastreamento
              (incluindo GA4). Você configura tags e triggers no console do GTM, sem tocar em
              código. O <strong>GA4 ID</strong> aqui é apenas referência para o admin — a tag do
              GA4 propriamente dita é configurada dentro do GTM.
            </p>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label htmlFor="gtm_id" className="block text-xs font-mono uppercase tracking-widest text-neutral-500 mb-1">
              Google Tag Manager ID
            </label>
            <input
              id="gtm_id"
              type="text"
              data-testid="settings-gtm-input"
              value={settings.gtm_id || ''}
              onChange={(e) => setSettings((s) => ({ ...s, gtm_id: e.target.value }))}
              placeholder="GTM-TQ9BW6KM"
              className="w-full px-4 py-2.5 border border-neutral-300 rounded-lg font-mono text-sm focus:border-accent-orange focus:ring-1 focus:ring-accent-orange outline-none"
            />
            <p className="text-xs text-zinc-700 mt-1">Formato: <code>GTM-XXXXXXX</code></p>
          </div>
          <div>
            <label htmlFor="ga_id" className="block text-xs font-mono uppercase tracking-widest text-neutral-500 mb-1">
              Google Analytics ID (referência)
            </label>
            <input
              id="ga_id"
              type="text"
              data-testid="settings-ga-input"
              value={settings.ga_id || ''}
              onChange={(e) => setSettings((s) => ({ ...s, ga_id: e.target.value }))}
              placeholder="G-Z52WGQGCRB"
              className="w-full px-4 py-2.5 border border-neutral-300 rounded-lg font-mono text-sm focus:border-accent-orange focus:ring-1 focus:ring-accent-orange outline-none"
            />
            <p className="text-xs text-zinc-700 mt-1">Formato: <code>G-XXXXXXXXXX</code> (só referência)</p>
          </div>
        </div>
        <div className="flex justify-end mt-4">
          <button
            type="button"
            onClick={saveAnalytics}
            disabled={savingSettings}
            data-testid="settings-analytics-save-btn"
            className="px-6 py-2.5 bg-accent-orange text-white rounded-lg text-sm font-bold hover:bg-[#E55D00] transition-colors disabled:opacity-50"
          >
            {savingSettings ? 'Salvando…' : 'Salvar Analytics'}
          </button>
        </div>
        <div className="mt-4 text-xs text-neutral-500 bg-blue-50 border border-blue-100 rounded-lg px-3 py-2 flex items-start gap-2">
          <iconify-icon icon="solar:info-circle-linear" className="text-blue-500 text-base mt-0.5 flex-shrink-0"></iconify-icon>
          <span>
            <strong>Como funciona:</strong> o <code>dataLayer</code> é inicializado antes do React montar (via <code>public/index.html</code>) e o loader do GTM é injetado dinamicamente pelo <code>SettingsContext</code> assim que o backend responde. Ao trocar o ID acima, faça F5 na página pública para o novo container começar a ser carregado. Os triggers de <em>History Change</em> configurados dentro do GTM capturam as mudanças de rota do SPA automaticamente.
          </span>
        </div>
      </div>


      {/* Health banner */}
      <div
        className={`rounded-xl border p-5 mb-8 flex items-start gap-4 ${
          healthOk
            ? 'bg-green-50 border-green-200'
            : 'bg-orange-50 border-orange-200'
        }`}
        data-testid="system-health-banner"
      >
        <iconify-icon
          icon={healthOk ? 'solar:check-circle-bold' : 'solar:danger-triangle-bold'}
          className={`text-3xl ${healthOk ? 'text-green-600' : 'text-accent-orange'}`}
        ></iconify-icon>
        <div className="flex-1">
          <h3 className="font-bold text-lg">
            {healthOk ? 'Banco de dados saudável' : 'Atenção necessária'}
          </h3>
          <p className="text-sm text-neutral-700 mt-1">
            {healthOk && 'Todas as coleções críticas têm dados e a migração 0010 já foi aplicada.'}
            {!syncDirOk && 'Pasta de snapshots ausente no deploy. '}
            {syncDirOk && !m0010Applied && 'Migração 0010_full_preview_sync nunca foi aplicada. '}
            {syncDirOk && m0010Applied && emptyCritical.length > 0 && (
              `Coleções vazias: ${emptyCritical.join(', ')}. `
            )}
            {!healthOk && 'Use o botão "Sincronizar agora" abaixo.'}
          </p>
        </div>
      </div>

      {/* Action panel */}
      <div className="bg-white rounded-xl border border-neutral-200 p-6 mb-8" data-testid="sync-actions">
        <h3 className="text-lg font-bold mb-1">Sincronizar dados da Preview</h3>
        <p className="text-sm text-neutral-500 mb-5">
          Reaplica os snapshots JSON em <code className="text-xs font-mono bg-neutral-100 px-1.5 py-0.5 rounded">backend/migrations/data/full_sync/</code> no banco atual.
          A operação é <strong>idempotente</strong> (não duplica registros).
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <button
            onClick={() => runSync({ reset: false })}
            disabled={syncing}
            data-testid="sync-incremental-btn"
            className="accent-glow-card p-5 border-2 border-accent-orange rounded-lg text-left disabled:opacity-50 disabled:cursor-not-allowed bg-accent-orange/5 hover:bg-accent-orange/10 transition-colors"
          >
            <div className="flex items-center gap-3 mb-2">
              <iconify-icon icon="solar:refresh-circle-bold" className="text-3xl text-accent-orange"></iconify-icon>
              <h4 className="font-bold">Sincronizar agora</h4>
            </div>
            <p className="text-sm text-neutral-600">
              Executa apenas a migração <code className="text-xs font-mono">0010_full_preview_sync</code>.
              Recomendado para popular conteúdo no ambiente atual.
            </p>
          </button>

          <button
            onClick={() => runSync({ reset: true })}
            disabled={syncing}
            data-testid="sync-reset-btn"
            className="accent-glow-card p-5 border-2 border-dashed border-neutral-300 rounded-lg text-left disabled:opacity-50 disabled:cursor-not-allowed hover:bg-neutral-50 transition-colors"
          >
            <div className="flex items-center gap-3 mb-2">
              <iconify-icon icon="solar:restart-bold" className="text-3xl text-neutral-700"></iconify-icon>
              <h4 className="font-bold">Re-executar TUDO</h4>
            </div>
            <p className="text-sm text-neutral-600">
              Limpa o registro de migrations aplicadas e re-executa as 10
              migrações em ordem. Use se algo estiver realmente quebrado.
            </p>
          </button>
        </div>

        {syncing && (
          <div className="mt-4 flex items-center gap-3 text-sm text-accent-orange" data-testid="sync-loading">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-accent-orange"></div>
            <span>Executando sincronização… isso pode levar alguns segundos.</span>
          </div>
        )}

        {lastSyncResult && !syncing && (
          <div className="mt-5 p-4 bg-neutral-50 border border-neutral-200 rounded-lg" data-testid="sync-result">
            <h5 className="font-bold text-sm mb-3">Último resultado</h5>
            {Object.keys(lastSyncResult.deltas || {}).length === 0 ? (
              <p className="text-sm text-neutral-600">
                Nenhuma diferença encontrada — banco já estava sincronizado.
              </p>
            ) : (
              <div className="space-y-1">
                {Object.entries(lastSyncResult.deltas).map(([coll, d]) => (
                  <div key={coll} className="flex items-center justify-between text-sm font-mono">
                    <span>{coll}</span>
                    <span>
                      <span className="text-zinc-700">{d.before}</span>
                      <span className="mx-2">→</span>
                      <span className="font-bold">{d.after}</span>
                      <span className={`ml-2 px-2 py-0.5 rounded text-xs ${
                        d.added > 0 ? 'bg-green-100 text-green-700' : 'bg-neutral-200 text-neutral-600'
                      }`}>
                        {d.added > 0 ? `+${d.added}` : d.added}
                      </span>
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Database state */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        <div className="bg-white rounded-xl border border-neutral-200 p-6" data-testid="db-counts">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-lg font-bold">Estado das coleções</h3>
              <p className="text-sm text-neutral-500">
                Banco: <code className="font-mono">{status?.db_name}</code>
              </p>
            </div>
            <button
              onClick={loadStatus}
              className="p-2 hover:bg-neutral-100 rounded-lg transition-colors"
              title="Atualizar"
              data-testid="refresh-status-btn"
            >
              <iconify-icon icon="solar:refresh-linear" className="text-xl"></iconify-icon>
            </button>
          </div>
          <div className="space-y-1.5">
            {Object.entries(counts).map(([coll, count]) => {
              const isCritical = CRITICAL_COLLECTIONS.includes(coll);
              const isEmpty = count === 0;
              return (
                <div
                  key={coll}
                  className="flex items-center justify-between py-1.5 px-3 rounded-md hover:bg-neutral-50"
                  data-testid={`count-${coll}`}
                >
                  <span className="font-mono text-sm">{coll}</span>
                  <span className={`text-sm font-bold ${
                    isCritical && isEmpty ? 'text-red-600' : 'text-neutral-900'
                  }`}>
                    {typeof count === 'number' ? count : 'error'}
                  </span>
                </div>
              );
            })}
          </div>
        </div>

        <div className="bg-white rounded-xl border border-neutral-200 p-6" data-testid="sync-files">
          <h3 className="text-lg font-bold mb-1">Snapshots disponíveis</h3>
          <p className="text-sm text-neutral-500 mb-4">
            {syncDirOk ? (
              <>Pasta encontrada em <code className="text-xs font-mono">{status?.sync_data_dir}</code></>
            ) : (
              <span className="text-red-600">
                ⚠ Pasta NÃO encontrada — o deploy não está incluindo os snapshots.
              </span>
            )}
          </p>
          {syncDirOk && syncFilesCount > 0 ? (
            <div className="space-y-1.5">
              {status.sync_data_files.map((f) => (
                <div key={f.name} className="flex items-center justify-between py-1.5 px-3 rounded-md hover:bg-neutral-50">
                  <span className="font-mono text-xs">{f.name}</span>
                  <span className="text-xs text-neutral-500">
                    {f.size_bytes != null ? `${(f.size_bytes / 1024).toFixed(1)} KB` : f.error}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-zinc-700 py-6 text-center">Nenhum snapshot encontrado.</p>
          )}
        </div>
      </div>

      {/* Migrations history */}
      <div className="bg-white rounded-xl border border-neutral-200 p-6" data-testid="migrations-history">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-bold">Migrations aplicadas</h3>
            <p className="text-sm text-neutral-500">{totalApplied} de 10 esperadas</p>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-neutral-500 uppercase tracking-widest font-mono border-b">
                <th className="py-2 pr-4">ID</th>
                <th className="py-2 pr-4">Aplicada em</th>
                <th className="py-2">Stats</th>
              </tr>
            </thead>
            <tbody>
              {(status?.applied_migrations || []).map((m) => (
                <tr key={m._id} className="border-b border-neutral-100" data-testid={`migration-${m._id}`}>
                  <td className="py-2 pr-4 font-mono text-xs">{m._id}</td>
                  <td className="py-2 pr-4 text-xs text-neutral-500">
                    {m.applied_at ? new Date(m.applied_at).toLocaleString('pt-BR') : '—'}
                  </td>
                  <td className="py-2 text-xs text-neutral-500 truncate max-w-md">
                    {m.stats ? JSON.stringify(m.stats).slice(0, 100) : '—'}
                  </td>
                </tr>
              ))}
              {totalApplied === 0 && (
                <tr>
                  <td colSpan={3} className="py-6 text-center text-zinc-700">
                    Nenhuma migration aplicada ainda.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default SystemDiagnostics;
